import { map, mergeMap, toArray, catchError } from "rxjs/operators";
import { from, Observable, of } from "rxjs";
import HttpClient from "./HttpClient";
import { APIHosts, ApiReduxHelpers } from "./reduxHelpers";
import type { RequestOptions } from "./types";
import { isUserAdmin } from "@/utils/jwtHelper";
import { getCompanyId } from "@/helper/company.helper";

export interface FolderResponse {
  _id: string;
  name_folder: string;
  path_folder: string;
  project_id: string;
  parent_id?: string;
  created_at?: string;
  updated_at?: string;
  results?: any;
}

export type WorkspaceType = "My Drive" | "Shared with me" | "Team Drives" | "Trash";

export interface FolderContextResponse {
  id: string;
  name: string;
  color?: string;
  type: string;
  labelCode?: string;
  parentId: string | null;
  createUser: {
    name: string;
    icon: string | null;
    id: string;
    email: string;
  };
  createdDate: string;
  lastModified: string;
  isOrganization: boolean;
  clientId: string | null;
  labelChildren: any[] | null;
  documentChildren: any[] | null;
  fileType: number;
  transferedUser: any | null;
  path: Array<{
    id: string;
    name: string;
  }>;
  ownerRootId: string;
  workspace: WorkspaceType;
}

export interface FileAccessContextResponse {
  data: {
    id: string;
    cloudLink: string;
    typeStorage: string;
    clientId: string | null;
    bucketName: string;
    isDeleted: boolean;
    isActive: boolean;
    connectionId: string;
    isOrganization: boolean;
    displayName: string;
    createDate: string;
    lastModified: string;
    owner: {
      name: string;
      icon: string | null;
      id: string;
      email: string;
    };
    fileId: string;
    fileCode: string | null;
    name: string;
    type: string;
    jsonContent: any | null;
    isPublish: boolean;
    parentId: string;
    version: string;
    size: number;
  };
  path: Array<{
    id: string;
    name: string;
  }>;
  ownerRootId: string;
  workspace: WorkspaceType;
}

export interface LabelChild {
  idChildren: string;
  type: string;
}

export interface CreateUser {
  name: string;
  icon?: string | null;
  id: string;
  email?: string;
}

export interface LabelData {
  name: string;
  color?: string;
  type: string;
  labelCode?: string;
  children?: LabelChild[];
  parentId?: string | null;
  createUser?: CreateUser;
  transferedUser?: CreateUser;
}

class FolderApiController extends ApiReduxHelpers {
  constructor() {
    super();
  }

  ApiHost = APIHosts.Upload;

  public Get = {
    getFolderByProjectId: (project_id: string, options?: RequestOptions) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Document/project/${project_id}`;
      return HttpClient.get(url, options).pipe(map((res) => res));
    },
    
    /**
     * Get folder context for link sharing
     * Resolves workspace, breadcrumb path, and permissions
     * @param folderId - The folder ID from URL
     * @param deep - Depth level for children (default: 0)
     */
    getFolderContext: (
      folderId: string,
      deep: number = 0,
      options?: RequestOptions
    ): Observable<FolderContextResponse> => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/${folderId}?deep=${deep}`;
      return HttpClient.get(url, options).pipe(map((res) => res as FolderContextResponse));
    },

    /**
     * Get file access context for link sharing
     * Resolves workspace, breadcrumb path, and file details
     * @param fileId - The file ID from URL
     */
    getFileAccessContext: (
      fileId: string,
      options?: RequestOptions
    ): Observable<FileAccessContextResponse> => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Document/access/file/${fileId}`;
      return HttpClient.get(url, options).pipe(map((res) => res as FileAccessContextResponse));
    },
   getFilesByFolderId: (
    folderId: string,
    params?: Record<string, any>
  ): Observable<any> => {
    const queryString = params
      ? "?" + new URLSearchParams(params as Record<string, string>).toString()
      : "";
    const url = `${this.getHost(
      this.ApiHost
    )}/Label/files/${folderId}${queryString}`;
    return HttpClient.get(url).pipe(map((res) => res));
  },
  
   getItemsDeletedFromMyDrive: (
    folderId: string,
    params?: Record<string, any>
  ): Observable<any> => {
    const queryString = params
      ? "?" + new URLSearchParams(params as Record<string, string>).toString()
      : "";
    const url = `${this.getHost(
      this.ApiHost
    )}/Label/files/${folderId}/trash${queryString}`;
    return HttpClient.get(url).pipe(map((res) => res));
  },
  
   getTeamFilesByFolderId: (
    folderId: string,
    params?: Record<string, any>
  ): Observable<any> => {
    const queryString = params
      ? "?" + new URLSearchParams(params as Record<string, string>).toString()
      : "";
    const url = `${this.getHost(
      this.ApiHost
    )}/Label/files/org/${folderId}${queryString}`;
    return HttpClient.get(url).pipe(map((res) => res));
  },
    
    getDocumentById: (document_id: string, options?: RequestOptions) => {
      const host = this.getHost(this.ApiHost);
      const url = `${host}/Document/${document_id}`;
      return HttpClient.get(url, options).pipe(map((res) => res));
    },
    downloadFileById: (file_id: string, options?: RequestOptions) => {
      const host = this.getHost(this.ApiHost);
      const url = `${host}/Document/downloadFile/${file_id}`;
      return HttpClient.get(url, { ...options, responseType: "blob" }).pipe(
        map((res) => res)
      );
    },
    getFolderRootIdByProjectId: (projectId: number): Observable<string> => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Document/getFolderRootId/${projectId}`;
      return HttpClient.get(url).pipe(map((res) => res as string));
    },
    getFilesByRootId: (
      rootFolderId: string,
      params?: Record<string, any>
    ): Observable<any> => {
      const queryString = params
        ? "?" + new URLSearchParams(params as Record<string, string>).toString()
        : "";
      const url = `${this.getHost(
        this.ApiHost
      )}/Document/fileRoots/${rootFolderId}${queryString}`;
      return HttpClient.get(url).pipe(map((res) => res));
    },
    getTeamStorageUsage: (
      labelId: string,
      options?: RequestOptions
    ): Observable<any> => {
      const companyId = getCompanyId();
      const url = `${this.getHost(this.ApiHost)}/TeamStorageUsage/${labelId}?companyId=${companyId}`;
      return HttpClient.get(url, options).pipe(map((res) => res));
    },
  };

  public Post = {
    downloadFolder: (
      labelId: string,
      zipFileName: string,
      includeSubfolders: boolean = true
    ): Observable<Blob> => {
      const url = `${this.getHost(this.ApiHost)}/api/Download/DownloadFolder/${labelId}`;
      const data = {
        labelId,
        includeSubfolders,
        zipFileName,
      };
      // console.log("Download folder URL:", url);
      // console.log("Download folder payload:", data);
      return HttpClient.post<Blob>(url, data, {
        responseType: "blob",
      }).pipe(map((res) => res));
    },

    createNewFolder: (
      data: LabelData,
      projectId: string,
      options?: RequestOptions
    ) => {
      // isOrganization will be passed from the component based on currentWorkspace
      const bodyData = {
        ...data,
      };

      const url = `${this.getHost(this.ApiHost)}/Label/${projectId}`;
      return HttpClient.post(url, bodyData, options).pipe(map((res) => res));
    },
    createFolderRootProject: (
      data: LabelData,
      projectId: string,
      options?: RequestOptions,
      companyId?: string
    ) => {
      const params = new URLSearchParams({ projectId });
      if (companyId) params.append("companyId", companyId);
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/createFolderRootProject?${params.toString()}`;
      return HttpClient.post(url, data, options).pipe(map((res) => res));
    },
    uploadFile: (
      formData: FormData,
      setProgress: (progress: number) => void,
      companyId?: string,
      labelId?: string,
      isPublish: boolean = false,
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Document/file/upload?companyId=${companyId || ""}&labelid=${
        labelId || ""
      }&isPublish=${isPublish}`;
      return HttpClient.upload(url, setProgress, formData, options);
    },
    getUploadFileUrl: (
      companyId?: string,
      labelId?: string,
      isPublish: boolean = false
    ) => {
      return `${this.getHost(this.ApiHost)}/Document/file/upload?companyId=${
        companyId || ""
      }&labelid=${labelId || ""}&isPublish=${isPublish}`;
    },
  };

  public Put = {
    transferOwner: (
      folderId: string,
      transferedUser: {
        name: string;
        icon: string | null;
        id: string;
        email: string;
      },
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(this.ApiHost)}/Label/${folderId}`;
      return HttpClient.put(url, { transferedUser }, options).pipe(
        map((res) => res)
      );
    },
    updateLabel: (
      labelId: string,
      body: { parentId: string },
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(this.ApiHost)}/Label/${labelId}`;

      return HttpClient.put(url, body, options).pipe(map((res) => res));
    },
updateTeamStorageQuota: (
      labelId: string,
      quota: number | null, 
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/TeamStorageUsage/${labelId}/quota`;
      return HttpClient.put(url, quota, options).pipe(map((res) => res));
    },
  };

  public Patch = {
    markFolderAsDeleted: (folder_id: string, options?: RequestOptions) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/${folder_id}?isDeleted=true`;
      return HttpClient.patch(url, {}, options).pipe(map((res) => res));
    },
    markFoldersAsDeleted: (
      folderIds: string[],
      isDeleted: boolean = true,
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/multi/delete?isDeleted=${isDeleted}`;
      return HttpClient.patch(url, folderIds, options).pipe(
        map((res) => ({ success: true, results: res }))
      );
    },
    restoreFolders: (folderIds: string[], options?: RequestOptions) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/multi/delete?isDeleted=false`;
      return HttpClient.patch(url, folderIds, options).pipe(
        map((res) => ({ success: true, results: res }))
      );
    },
    moveItem: (itemId: string, parentId: string) => {
      const url = `${this.getHost(this.ApiHost)}/Document`;
      const data = {
        id: itemId,
        parentId: parentId,
      };
      return HttpClient.patch(url, data).pipe(map((res) => res));
    },
    moveMultipleFolders: (
      labelIds: string[],
      parentId: string,
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(this.ApiHost)}/Label/multi/update`;
      const data = {
        labelIds,
        data: {
          parentId,
        },
      };
      return HttpClient.patch(url, data, options).pipe(map((res) => res));
    },
    moveMultipleFiles: (
      fileIds: string[],
      parentId: string,
      options?: RequestOptions
    ) => {
      const url = `${this.getHost(this.ApiHost)}/Document/multi/update`;
      const data = {
        fileIds,
        parentId,
      };
      return HttpClient.patch(url, data, options).pipe(map((res) => res));
    },
  };

  public Delete = {
    deleteFolderById: (folder_id: string, options?: RequestOptions) => {
      const url = `${this.getHost(this.ApiHost)}/Document/${folder_id}`;
      return HttpClient.delete(url, options).pipe(map((res) => res));
    },
    deleteFoldersByIds: (folderIds: string[], options?: RequestOptions) => {
      const url = `${this.getHost(this.ApiHost)}/Label/deleteLabels`;
      return HttpClient.delete(url, folderIds, options).pipe(map((res) => res));
    },
    deleteFileById: (file_id: string, options?: RequestOptions) => {
      const url = `${this.getHost(this.ApiHost)}/Document/${file_id}`;
      return HttpClient.delete(url, options).pipe(map((res) => res));
    },
    forceDeleteFolder: (folder_id: string, options?: RequestOptions) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/force-delete/${folder_id}`;
      return HttpClient.delete(url, {}, options).pipe(map((res) => res));
    },
    forceDeleteFolders: (folderIds: string[], options?: RequestOptions) => {
      const companyId = localStorage.getItem('companyId') || '1';
      const url = `${this.getHost(
        this.ApiHost
      )}/Label/multi/force-delete?companyId=${companyId}`;
      return HttpClient.delete<any[]>(url, folderIds, options);
    },
  };
}

export const FolderService = new FolderApiController();
